<script setup lang="ts">
import { nextTick, onMounted, ref, toRefs, watch } from 'vue';

const model = ref('');
const qInputRef = ref();

// eslint-disable-next-line no-undef
const props = withDefaults(defineProps<{
  modelDefaultValue: string
   type?: 'number' | 'text' | 'textarea' | 'time' | 'password' | 'email' | 'search' | 'tel' | 'file' | 'url' | 'date'
   outlined?: boolean
   dense?: boolean
   clearable?: boolean
   disable?: boolean
   placeholder?: string
   formatter?:(value: string) => string
 }>(), {
  type: 'text',
  outlined: false,
  dense: true,
  clearable: false,
  disable: false,
  placeholder: '',
  formatter: undefined
});

const emit = defineEmits<{
  model: [value: string]
  apply: []
}>();

const { modelDefaultValue } = toRefs(props);

// Applies the formatter (if any) and, when it strips characters the browser
// already inserted into the native input, forces the DOM back in sync: a
// reactive assignment that resolves to the same value it already held
// (e.g. normalizing "032" back to "32" when the field already read "32")
// never triggers a Vue re-render, so the stray character would otherwise
// stay visible even though the model is correct.
const applyFormattedValue = (rawValue: string): void => {
  const formattedValue = props.formatter ? props.formatter(rawValue) : rawValue;

  model.value = formattedValue;
  emit('model', formattedValue);

  if (formattedValue === rawValue) {
    return;
  }

  const inputEl: HTMLInputElement | undefined = qInputRef.value?.getNativeElement?.();

  if (!inputEl) {
    return;
  }

  const cursorShift = rawValue.length - formattedValue.length;
  const cursorPosition = Math.max(0, (inputEl.selectionStart ?? formattedValue.length) - cursorShift);

  nextTick(() => {
    inputEl.value = formattedValue;
    inputEl.setSelectionRange(cursorPosition, cursorPosition);
  });
};

watch(modelDefaultValue, (value): void => {
  model.value = value;
});

onMounted(() => {
  model.value = modelDefaultValue.value;
});
</script>

<template>
  <q-input
    ref="qInputRef"
    :model-value="model"
    :type="type"
    :outlined="outlined"
    :dense="dense"
    :clearable="clearable"
    :disable="disable"
    :placeholder="placeholder"
    @update:model-value="applyFormattedValue"
    @keyup.enter="$emit('apply')"
  />
</template>
